import { useCallback, useEffect, useState } from "react";
import type { AdvertisedPriceDetailsResponseProto } from "@noom/noom-contracts/noom_contracts/healthcare/service/healthcare/advertised_price_service";

import { getInsuranceFulfillmentDetails } from "../modules/refill/getInsuranceFulfillmentDetails";
import { formatPrice } from "../modules/refill/formatPrice";

import { useAuthCredentials } from "./useAuthCredentials";

const PHARMACY_DISPLAY_NAMES: Record<string, string> = {
  PHARMACY_HUB: "Pharmacy Hub",
  HEALTH_WAREHOUSE: "Health Warehouse",
  WELLS_OCALA: "Wells Pharmacy",
  WELLS_DALLAS: "Wells Pharmacy",
  TAILOR_MADE_COMPOUNDING: "Tailor Made Compounding",
  OPTIO_RX: "Optio Rx",
  NOVOCARE: "NovoCare",
  LILLY_DIRECT: "LillyDirect",
  AMAZON_PHARMACY: "Amazon Pharmacy",
};

const toPharmacyDisplayName = (enumValue: string): string =>
  PHARMACY_DISPLAY_NAMES[enumValue] ?? enumValue;

export type InsuranceFulfillmentDetails = {
  pharmacyDisplayName: string;
  fulfillmentPartner: string;
  formattedPrice: string;
};

export const useInsuranceFulfillmentDetails = (
  orderId: string | null
): {
  data: InsuranceFulfillmentDetails | null;
  loading: boolean;
  error: string | null;
} => {
  const { accessCode, accessToken } = useAuthCredentials();
  const [data, setData] = useState<InsuranceFulfillmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!accessCode || !orderId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response: AdvertisedPriceDetailsResponseProto =
        await getInsuranceFulfillmentDetails(
          accessCode,
          accessToken ?? "",
          orderId
        );
      setData({
        pharmacyDisplayName: toPharmacyDisplayName(response.fulfillmentPartner),
        fulfillmentPartner: response.fulfillmentPartner,
        formattedPrice: formatPrice(response.advertisedPrice),
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch advertised price details"
      );
    } finally {
      setLoading(false);
    }
  }, [accessCode, accessToken, orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error };
};
